import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Spinner from 'components/spinner/Spinner';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import GraphOverTime from 'components/graphs/graph-over-time/GraphOverTime';
import BarGraphLazy from 'components/graphs/echarts/bar-graph/BarGraphLazy';

import InfluencerChannelOverview from '../InfluencerChannelOverview';

import { PROFILE_DETAILS_TABS, PROFILE_GRAPHS_DATE_SELECTION_ITEMS, getProfileGraphsDateSelectionOptions } from 'utils/influencers';
import { getProfileGraphOverTimeOptions } from 'utils/graphs/profile-graphs/profile-graphs';
import { getData } from 'components/graphs/echarts/bar-graph/utils/bar-graph';
import { __t, applyTranslations } from 'utils/language';

import type {
  Channel,
  Data,
  GraphOverTimeProfileItem,
  MixedSelectDropdownItem
} from 'types';

import './influencer-profile-tabs-section.scss';

applyTranslations(require('./influencer-profile-tabs-section.i18n.json'));

type Props = {
  activeChannel: string,
  influencerGraphs: Array<GraphOverTimeProfileItem>,
  influencerGraphsStartDate: string,
  activeTab: string,
  selectedDateRangeId: string,
  selectedChannel: Channel | undefined | null,
  isLoading: boolean,
  onClick: (
    identifier: string | undefined | null,
    type: string | undefined | null,
    isActive: boolean
  ) => void,
  onChange: (value: string) => void
};

type State = {
  dateRangeSelected: boolean
};

class InfluencerProfileTabsSection extends React.PureComponent<Props, State> {
  state = {
    dateRangeSelected: false
  };

  getFilteredData(): any {
    const { influencerGraphs, activeTab } = this.props;

    return influencerGraphs.filter(item => Boolean(item[activeTab]));
  }

  getDateDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { influencerGraphsStartDate, selectedDateRangeId } = this.props;
    const updatedOptions = getProfileGraphsDateSelectionOptions(influencerGraphsStartDate);

    return updatedOptions.map(item => ({
      identifier: item.id,
      isDisabled: false,
      isSelected: selectedDateRangeId === item.id,
      label: item.label,
      value: item.id
    }));
  };

  getGraphOptions(): any {
    const { activeTab, selectedDateRangeId, activeChannel } = this.props;
    const filteredData = this.getFilteredData();
    const selectedDateRangeItem = PROFILE_GRAPHS_DATE_SELECTION_ITEMS.find(item => item.id === selectedDateRangeId);
    const interval = selectedDateRangeItem?.interval || 'month';

    if (activeTab === 'videoViews') {
      return getData(filteredData, activeTab, activeChannel, interval);
    }

    return getProfileGraphOverTimeOptions(filteredData, activeTab, interval, activeChannel);
  }

  handleDateRangeSelect = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { onChange } = this.props;

    this.setState({
      dateRangeSelected: true
    });

    onChange(selectedItem?.identifier || updatedItems[0].identifier);
  };

  renderNoDataOverlay(): React.ReactNode {
    const { dateRangeSelected } = this.state;

    const overlayClasses = classnames({
      'influencer-profile-tabs-section__graph-overlay': true,
      'influencer-profile-tabs-section__graph-overlay--selected-range': dateRangeSelected
    });

    return (
      <div className={overlayClasses}>
        {__t('influencer_profile_tabs_section.label_no_data')}
      </div>
    );
  }

  renderGraph(activeTab: string, options: any): React.ReactNode {
    const filteredData = this.getFilteredData();

    if (activeTab === 'videoViews') {
      return (
        <React.Fragment>
          {
            !isEmpty(filteredData) && (
              <div className="influencer-profile-tabs-section__graph-help">
                {__t('influencer_profile_tabs_section.navigation_help')}
              </div>
            )
          }
          <BarGraphLazy
            key={activeTab}
            data={options}
          />
        </React.Fragment>
      );
    }

    return (
      <GraphOverTime
        key={activeTab}
        options={options}
        size="large"
      />
    );
  }

  renderGraphContainer(activeTab: string): React.ReactNode {
    const { selectedDateRangeId, influencerGraphsStartDate } = this.props;
    const options = this.getGraphOptions();
    const dateRangeSelectorClasses = classnames('influencer-profile-tabs-section__date-range-selector');
    const filteredData = this.getFilteredData();
    const dateDropdownItems = this.getDateDropdownItems();

    return (
      <div className="influencer-profile-tabs-section__graph-container">
        {
          !isEmpty(filteredData) && influencerGraphsStartDate && (
            <div className={dateRangeSelectorClasses}>
              <div className="influencer-profile-tabs-section__date_range_header">
                {__t('influencer_profile_tabs_section.date_range')}
              </div>
              <MixedSelectDropdown
                key={`influencer-profile-tabs-section__dropdown--${selectedDateRangeId}`}
                name="influencer-profile-tabs-section__dropdown"
                items={dateDropdownItems}
                withSelectedInList
                className="influencer-profile-tabs-section__dropdown"
                onItemsToggle={this.handleDateRangeSelect}
              />
            </div>
          )
        }
        {
          !isNil(options) && !isEmpty(options) && (
            <div className="influencer-profile-tabs-section__graph">
              {this.renderGraph(activeTab, options)}
            </div>
          )
        }
        {
          (isNil(options) || isEmpty(options)) &&
            this.renderNoDataOverlay()
        }
      </div>
    );
  }

  renderContent(): React.ReactNode | undefined | null {
    const { activeTab, selectedChannel } = this.props;

    if (activeTab === 'followers' || activeTab === 'engagementRate' || activeTab === 'videoViews') {
      return this.renderGraphContainer(activeTab);
    }

    if (activeTab === 'overview') {
      return (
        <InfluencerChannelOverview selectedChannel={selectedChannel} />
      );
    }

    return null;
  }

  render(): React.ReactNode {
    const { activeTab, isLoading, onClick, activeChannel } = this.props;

    const containerClasses = classnames({
      'influencer-profile-tabs-section__container': true,
      'influencer-profile-tabs-section__container--loading': isLoading
    });

    return (
      <div
        className="influencer-profile-tabs-section"
        data-qa="influencer-profile-tabs-section"
      >
        <div className="influencer-profile-tabs-section__title">
          {__t('influencer_profile_tabs-section.label_account_analysis')}
        </div>
        <div className="influencer-profile-tabs-section__container-border">
          <TabsSection
            items={PROFILE_DETAILS_TABS(activeChannel)}
            activeTab={activeTab}
            onClick={onClick}
          />
          <div className={containerClasses}>
            {
              isLoading ?
                <Spinner
                  position="inline"
                  size="small"
                  color="light"
                /> :
                this.renderContent()
            }
          </div>
        </div>
      </div>
    );
  }
}

export default InfluencerProfileTabsSection;
